import { z } from 'zod';
import { gitCommitShaSchema, instantSchema, positiveSafeIntegerSchema, semverSchema, sha256Schema, } from './primitives.js';
import { artifactVersionSchema } from './versioning.js';
export const compatibilityTupleSchema = z.strictObject({
    client_version: semverSchema,
    indexeddb_schema_version: positiveSafeIntegerSchema,
    api_contract_version: semverSchema,
    catalog_format_version: semverSchema.nullable(),
    graph_format_version: semverSchema.nullable(),
});
export const compatibilityPolicySchema = z
    .strictObject({
    contract_version: z.literal('compatibility_policy.v1'),
    policy_version: semverSchema,
    generated_at: instantSchema,
    allowed_tuples: z.array(compatibilityTupleSchema).min(1).max(1_000),
})
    .superRefine((value, context) => {
    const keys = value.allowed_tuples.map(compatibilityTupleKey);
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: 'custom',
            path: ['allowed_tuples'],
            message: 'compatibility matrix cannot contain duplicate tuples',
        });
    }
});
export const compatibilityCheckInputSchema = compatibilityTupleSchema;
export const compatibilityDecisionSchema = z.strictObject({
    compatible: z.boolean(),
    reason: z.enum([
        'exact_tuple_allowed',
        'client_not_supported',
        'indexeddb_not_supported',
        'api_not_supported',
        'catalog_not_supported',
        'graph_not_supported',
        'combination_not_supported',
    ]),
    requires_client_update: z.boolean(),
    requires_full_resync: z.boolean(),
});
export const dataArtifactBindingSchema = z.strictObject({
    artifact: artifactVersionSchema,
    format_version: semverSchema,
    license_identifier: z.string().min(1).max(256),
    license_manifest_sha256: sha256Schema,
    redistribution_allowed: z.literal(true),
    attribution_sha256: sha256Schema,
});
export const buildMetadataSchema = z
    .strictObject({
    contract_version: z.literal('build_metadata.v1'),
    build_id_sha256: sha256Schema,
    built_at: instantSchema,
    commit_sha: gitCommitShaSchema,
    app_version: semverSchema,
    contracts_version: semverSchema,
    environment: z.enum(['development', 'test', 'staging', 'production']),
    compatibility: compatibilityTupleSchema,
    catalog: dataArtifactBindingSchema.nullable(),
    graph: dataArtifactBindingSchema.nullable(),
})
    .superRefine((value, context) => {
    if (value.environment === 'production' && (value.catalog === null || value.graph === null)) {
        context.addIssue({
            code: 'custom',
            message: 'production build metadata must bind exact catalog and graph artifacts',
        });
    }
    if ((value.catalog?.format_version ?? null) !== value.compatibility.catalog_format_version) {
        context.addIssue({
            code: 'custom',
            path: ['catalog'],
            message: 'catalog artifact format must match the compatibility tuple',
        });
    }
    if ((value.graph?.format_version ?? null) !== value.compatibility.graph_format_version) {
        context.addIssue({
            code: 'custom',
            path: ['graph'],
            message: 'graph artifact format must match the compatibility tuple',
        });
    }
    if (value.compatibility.client_version !== value.app_version) {
        context.addIssue({
            code: 'custom',
            path: ['compatibility', 'client_version'],
            message: 'build app_version and compatibility client_version must match',
        });
    }
});
export function evaluateCompatibility(policyInput, inputValue) {
    const policy = compatibilityPolicySchema.parse(policyInput);
    const input = compatibilityCheckInputSchema.parse(inputValue);
    const exact = policy.allowed_tuples.some((candidate) => compatibilityTupleKey(candidate) === compatibilityTupleKey(input));
    if (exact) {
        return {
            compatible: true,
            reason: 'exact_tuple_allowed',
            requires_client_update: false,
            requires_full_resync: false,
        };
    }
    const clientMatches = policy.allowed_tuples.filter((candidate) => candidate.client_version === input.client_version);
    if (clientMatches.length === 0) {
        return incompatible('client_not_supported', true, false);
    }
    const dbMatches = clientMatches.filter((candidate) => candidate.indexeddb_schema_version === input.indexeddb_schema_version);
    if (dbMatches.length === 0) {
        return incompatible('indexeddb_not_supported', false, false);
    }
    const apiMatches = dbMatches.filter((candidate) => candidate.api_contract_version === input.api_contract_version);
    if (apiMatches.length === 0) {
        return incompatible('api_not_supported', false, false);
    }
    if (!apiMatches.some((candidate) => candidate.catalog_format_version === input.catalog_format_version)) {
        return incompatible('catalog_not_supported', false, false);
    }
    if (!apiMatches.some((candidate) => candidate.graph_format_version === input.graph_format_version)) {
        return incompatible('graph_not_supported', false, false);
    }
    return incompatible('combination_not_supported', false, true);
}
function compatibilityTupleKey(value) {
    return [
        value.client_version,
        value.indexeddb_schema_version,
        value.api_contract_version,
        value.catalog_format_version ?? 'none',
        value.graph_format_version ?? 'none',
    ].join('\u0000');
}
function incompatible(reason, requiresClientUpdate, requiresFullResync) {
    return {
        compatible: false,
        reason,
        requires_client_update: requiresClientUpdate,
        requires_full_resync: requiresFullResync,
    };
}
//# sourceMappingURL=compatibility.js.map